import { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Search, Send, CheckCircle, AlertCircle, Mail } from '../icons';
import { searchCoachesAndPartners, createShare, type SearchResult } from '../../lib/result-shares';
import { useAuth } from '../../contexts/AuthContext';

interface ShareResultsModalProps {
  resultId: string;
  isOpen: boolean;
  onClose: () => void;
  onShared?: () => void;
}

export function ShareResultsModal({ resultId, isOpen, onClose, onShared }: ShareResultsModalProps) {
  const { user } = useAuth();
  const [email, setEmail] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selected, setSelected] = useState<SearchResult | null>(null);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setEmail('');
      setResults([]);
      setHasSearched(false);
      setSelected(null);
      setMessage('');
      setSuccess(false);
      setError(null);
    }
  }, [isOpen]);

  const handleSearch = useCallback(async () => {
    const trimmed = email.trim();
    if (!trimmed || !trimmed.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }
    setSearching(true);
    setError(null);
    setHasSearched(false);
    const res = await searchCoachesAndPartners(trimmed);
    setResults(res);
    setSearching(false);
    setHasSearched(true);
  }, [email]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleSubmit = async () => {
    if (!selected || !user) return;
    setSubmitting(true);
    setError(null);
    const { error: err } = await createShare(resultId, user.id, selected, message);
    setSubmitting(false);
    if (err) {
      setError(err);
      return;
    }
    setSuccess(true);
    onShared?.();
    setTimeout(() => {
      onClose();
    }, 1500);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
            onClick={onClose}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[85vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {success ? (
                <div className="p-8 text-center">
                  <div className="w-16 h-16 bg-sage-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-sage-600" />
                  </div>
                  <h3 className="freud-heading-xl text-chocolate-900 mb-2">Result Shared!</h3>
                  <p className="text-chocolate-500 text-sm">
                    Your assessment results have been shared with {selected?.name}.
                  </p>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between p-5 border-b border-cream-200">
                    <h3 className="freud-heading-xl text-chocolate-900">Share Your Results</h3>
                    <button onClick={onClose} className="p-2 rounded-lg text-chocolate-400 hover:bg-cream-100 transition-colors">
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="p-5 space-y-4">
                    <div className="flex items-start gap-2.5 bg-cream-50 rounded-xl p-3.5 border border-cream-200">
                      <Mail className="w-4 h-4 text-chocolate-400 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-chocolate-500 leading-relaxed">
                        Enter the exact email address of the coach or partner you want to share with. For privacy, you can only find someone if you already know their email.
                      </p>
                    </div>

                    <div className="flex gap-2">
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          setHasSearched(false);
                          setResults([]);
                          setSelected(null);
                          setError(null);
                        }}
                        onKeyDown={handleKeyDown}
                        placeholder="coach@example.com"
                        className="freud-input flex-1"
                        autoFocus
                      />
                      <button
                        onClick={handleSearch}
                        disabled={searching || !email.trim()}
                        className="freud-btn-primary px-4 inline-flex items-center justify-center gap-1.5 whitespace-nowrap"
                      >
                        <Search className="w-4 h-4" />
                        {searching ? '...' : 'Find'}
                      </button>
                    </div>

                    {error && (
                      <div className="flex items-center gap-2 bg-red-50 text-red-700 p-3 rounded-xl text-sm">
                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                        {error}
                      </div>
                    )}

                    {searching && (
                      <p className="text-sm text-chocolate-400 text-center py-2">Searching...</p>
                    )}

                    {!searching && hasSearched && results.length > 0 && !selected && (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-chocolate-500 mb-1">
                          {results.length === 1 ? 'Found 1 match:' : `Found ${results.length} matches:`}
                        </p>
                        {results.map((r) => (
                          <button
                            key={`${r.type}-${r.id}`}
                            onClick={() => setSelected(r)}
                            className="w-full flex items-center gap-3 p-3 rounded-xl border border-cream-200 hover:border-sage-300 hover:bg-sage-50 transition-all text-left"
                          >
                            <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-xs font-bold text-white ${r.type === 'coach' ? 'bg-sage-500' : 'bg-warm-500'}`}>
                              {r.type === 'coach' ? 'C' : 'P'}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-chocolate-900 truncate">{r.name}</p>
                              <p className="text-xs text-chocolate-400 truncate">{r.email}</p>
                            </div>
                            <span className="text-[10px] font-bold uppercase tracking-wide text-chocolate-400 bg-cream-100 px-2 py-1 rounded-full">
                              {r.type}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}

                    {!searching && hasSearched && results.length === 0 && !selected && (
                      <div className="text-center py-6 px-4">
                        <div className="w-12 h-12 bg-cream-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
                          <AlertCircle className="w-6 h-6 text-chocolate-400" />
                        </div>
                        <p className="text-sm font-medium text-chocolate-700 mb-1">No coach or partner found</p>
                        <p className="text-xs text-chocolate-400 leading-relaxed">
                          No account was found with that email address. Make sure you've entered the exact email they used to register.
                        </p>
                      </div>
                    )}

                    {selected && (
                      <div className="space-y-3">
                        <div className="flex items-center gap-3 p-3 rounded-xl bg-sage-50 border border-sage-200">
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-xs font-bold text-white ${selected.type === 'coach' ? 'bg-sage-500' : 'bg-warm-500'}`}>
                            {selected.type === 'coach' ? 'C' : 'P'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-chocolate-900 truncate">{selected.name}</p>
                            <p className="text-xs text-chocolate-400 truncate">{selected.email}</p>
                          </div>
                          <button onClick={() => setSelected(null)} className="p-1 rounded-lg text-chocolate-400 hover:bg-cream-100">
                            <X className="w-4 h-4" />
                          </button>
                        </div>

                        <div>
                          <label className="text-xs font-medium text-chocolate-600 mb-1.5 block">Personal message (optional)</label>
                          <textarea
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="Add a note to accompany your shared results..."
                            rows={3}
                            className="freud-input w-full resize-none"
                          />
                        </div>

                        {error && (
                          <div className="flex items-center gap-2 bg-red-50 text-red-700 p-3 rounded-xl text-sm">
                            <AlertCircle className="w-4 h-4 flex-shrink-0" />
                            {error}
                          </div>
                        )}

                        <button
                          onClick={handleSubmit}
                          disabled={submitting}
                          className="freud-btn-primary w-full inline-flex items-center justify-center gap-2"
                        >
                          <Send className="w-4 h-4" />
                          {submitting ? 'Sharing...' : `Share with ${selected.name}`}
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
